import { Document, Packer, Paragraph, TextRun, ExternalHyperlink, Footer, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import { xmlSafeText } from 'utils/XmlSafeTextUtil';

const LINE_SPACING = 240 * 1.15;
const LARGE_BOUNDARY = 480;
const SMALL_BOUNDARY = 120;

const STYLES = {
  paragraphStyles: [
    {
      id: 'Heading1',
      name: 'Heading 1',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 50 },
      paragraph: { spacing: { after: 120, before: 120 } },
    },
    {
      id: 'Heading2',
      name: 'Heading 2',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 36 },
      paragraph: { spacing: { after: 120, before: 120 } },
    },
    {
      id: 'Heading3',
      name: 'Heading 3',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 28 },
      paragraph: { spacing: { after: 120, before: 120 } },
    },
  ],
};

function getSpacing(i, arrayLength) {
  if (i === arrayLength - 1) {
    return { after: LARGE_BOUNDARY, line: LINE_SPACING, before: SMALL_BOUNDARY };
  }
  return { after: SMALL_BOUNDARY, line: LINE_SPACING, before: SMALL_BOUNDARY };
}

function buildTitleParagraphs(title) {
  if (!title) return [];
  return [
    new Paragraph({ text: title, heading: HeadingLevel.TITLE }),
    new Paragraph({}),
  ];
}

function buildCodeLinkParagraph(url, text, depth) {
  const headingLevels = [HeadingLevel.HEADING_1, HeadingLevel.HEADING_2, HeadingLevel.HEADING_3];
  const heading = headingLevels[Math.min(depth, 2)];
  return new Paragraph({
    heading,
    children: [
      new ExternalHyperlink({
        link: url,
        children: [new TextRun({ text, style: 'Hyperlink' })],
      }),
    ],
  });
}

function buildDescriptionParagraphs(textArray) {
  if (!textArray || textArray.length === 0) {
    return [new Paragraph({})];
  }
  return textArray.map((text, i) =>
    new Paragraph({ text, spacing: getSpacing(i, textArray.length) })
  );
}

function buildSnippetParagraphs(url, header, textArray) {
  if (!textArray || textArray.length === 0) return [];

  const linkParagraph = new Paragraph({
    indent: { left: 480, hanging: 0 },
    children: [
      new ExternalHyperlink({
        link: url,
        children: [new TextRun({ text: header, bold: true, style: 'Hyperlink' })],
      }),
    ],
  });

  const bodyParagraphs = textArray.map((text, i) =>
    new Paragraph({
      text,
      spacing: getSpacing(i, textArray.length),
      indent: { left: 480, hanging: 0 },
    })
  );

  return [linkParagraph, ...bodyParagraphs];
}

function buildCodeParagraphs(code) {
  const depth = (code.level || 1) - 1;
  const paragraphs = [
    buildCodeLinkParagraph(code.url, `${code.name} (${code.count})`, depth),
    ...buildDescriptionParagraphs(code.synthesisArray),
  ];
  for (const excerpt of code.excerpts) {
    paragraphs.push(...buildSnippetParagraphs(excerpt.url, excerpt.transcript_name, excerpt.textArray));
  }
  return paragraphs;
}

function buildFooter() {
  return new Footer({
    children: [
      new Paragraph({
        children: [
          new TextRun({ text: 'Created with Delve (delvetool.com)', color: 'b7b7b7' }),
        ],
      }),
    ],
  });
}

// Every string the builders hand to docx is sanitized here, once, before any
// builder runs. docx does not filter the characters XML forbids, and a single
// one anywhere in the package - a paragraph, the document title, a hyperlink
// target - makes the file unopenable, so the guarantee has to be structural
// rather than something each builder remembers to do.
//
// Lines are re-split after sanitizing because the sanitizer turns vertical tab
// and form feed into newlines, and the mapper split on newlines before we saw
// the text. A bare newline inside a run is not a break in Word - it renders as
// a space - so 'page one\fpage two' would otherwise be joined on one line.
function safeLines(lines) {
  return (lines || [])
    .filter((line) => line !== null && line !== undefined)
    .flatMap((line) => xmlSafeText(line).split('\n'))
    .filter((line) => line.length > 0);
}

function safeCode(code) {
  return {
    ...code,
    url: xmlSafeText(code.url),
    name: xmlSafeText(code.name),
    synthesisArray: safeLines(code.synthesisArray),
    excerpts: (code.excerpts || []).map((excerpt) => ({
      ...excerpt,
      url: xmlSafeText(excerpt.url),
      transcript_name: xmlSafeText(excerpt.transcript_name),
      textArray: safeLines(excerpt.textArray),
    })),
  };
}

export function buildDocument(title, codes) {
  const safeTitle = xmlSafeText(title);
  const children = [
    ...buildTitleParagraphs(safeTitle),
    ...(codes || []).map(safeCode).flatMap(buildCodeParagraphs),
  ];

  return new Document({
    title: safeTitle || '',
    styles: STYLES,
    sections: [{
      footers: { default: buildFooter() },
      children,
    }],
  });
}

export async function exportToDocx(projectName, fileName, codes) {
  const doc = buildDocument(projectName, codes);
  const blob = await Packer.toBlob(doc);
  saveAs(blob, fileName);
}

// Legacy default export for backwards compatibility
export default { exportToDocx };
